import {
  memo,
  useCallback,
  useEffect,
  useState,
} from "react";
import * as signalR from "@microsoft/signalr";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";

interface SignalRMessageDto {
  senderId: number;
  senderUserId: string;
  receiverId: number;
  receiverUserId: string;
  body: string;
  dateSent?: string;
}

const CONTAINER_MIN_HEIGHT = 200;

function SignalRMessageListenerInner() {
  const { instance, accounts } = useMsal();
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [messages, setMessages] = useState<SignalRMessageDto[]>([]);
  const [loading, setLoading] = useState(true);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (accounts.length === 0) throw new Error("No accounts found");

    try {
      const response = await instance.acquireTokenSilent({
        scopes: ["https://smbfglobal.onmicrosoft.com/user-api/user.read"],
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup({
          scopes: ["https://smbfglobal.onmicrosoft.com/user-api/user.read"],
        });
        return response.accessToken;
      }
      throw error;
    }
  }, [instance, accounts]);

  useEffect(() => {
    const connectToHub = async () => {
      try {
        const token = await getAccessToken();

        const newConnection = new signalR.HubConnectionBuilder()
          .withUrl("https://smbfglobal-g6bbhufugxchfncw.uksouth-01.azurewebsites.net/chatHub", {
            accessTokenFactory: async () => token,
          })
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Information)
          .build();

        newConnection.on("ReceiveMessage", (message: SignalRMessageDto) => {
          setMessages((prev) => [...prev, message]);
        });

        await newConnection.start();
        console.log("SignalR connected");
        setConnection(newConnection);
      } catch (err) {
        console.error("SignalR connection error:", err);
      } finally {
        setLoading(false);
      }
    };

    connectToHub();

    return () => {
      connection?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        padding: "1rem",
        border: "1px solid #ccc",
        borderRadius: 8,
        marginTop: "2rem",
        minHeight: CONTAINER_MIN_HEIGHT,
      }}
    >
      <h3>📨 Incoming Messages</h3>
      {loading && <p>Connecting to chat hub...</p>}
      {!loading && messages.length === 0 && <p>No messages received yet.</p>}
      <ul>
        {messages.map((msg, idx) => (
          <li key={idx} style={{ marginBottom: "1rem" }}>
            <strong>SenderId:</strong> {msg.senderId} <br />
            <strong>SenderUserId:</strong> {msg.senderUserId} <br />
            <strong>ReceiverId:</strong> {msg.receiverId} <br />
            <strong>ReceiverUserId:</strong> {msg.receiverUserId} <br />
            <strong>Body:</strong> {msg.body} <br />
            <strong>Date Sent:</strong> {msg.dateSent} <br />
          </li>
        ))}
      </ul>
    </div>
  );
}

SignalRMessageListenerInner.displayName = "SignalRMessageListener";

export default memo(SignalRMessageListenerInner);
